import {
  type Env,
  ENVS,
  type Platform,
  PLATFORMS,
  type Partition,
  PARTITIONS,
  PARTITION_DOMAINS,
} from './envConsts';

interface UrlParts {
  region: string;
  env: Env;
  platform: Platform;
  partition: Partition;
}

/**
 * Derives the partition from a URL by matching against the known partition
 * apex domains. FEDRAMP is checked before GovCloud because its domain
 * (`afg.us.autodesk.com`) is more specific. Anything else is COMMERCIAL.
 */
function getPartition(url: string): Partition {
  if (url.includes(PARTITION_DOMAINS[PARTITIONS.FEDRAMP_LEGACY])) {
    return PARTITIONS.FEDRAMP_LEGACY;
  }
  if (url.includes(PARTITION_DOMAINS[PARTITIONS.GOVCLOUD])) {
    return PARTITIONS.GOVCLOUD;
  }
  return PARTITIONS.COMMERCIAL;
}

const DEFAULT_ENV = ENVS.QA;
// eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
const DEFAULT_REGION = 'US';
// eslint-disable-next-line @adsk/r13n-best-practices/no-region-string-literal
const EMEA_REGION = 'EMEA';
const EMEA_EXTENSION = 'eu';

/**
 * Derives env, region, platform, and partition from an ACC hostname.
 *
 * Expects a bare hostname (e.g. `window.location.hostname`), optionally with a
 * protocol. The pattern is anchored, so a path, query, port, or trailing slash
 * makes it fall back to `QA`/`US` — callers holding a full URL must strip it first.
 */
export function parseURL(url: string): UrlParts {
  const pattern =
    /^(?:https?:\/\/)?(?:(?<subdomain>[a-zA-Z0-9-]+)\.)?(?<platform>acc|cpa|forma)(?:[-.](?<env>staging|qa|stg))?(?:\.(?<region>[^.]+))?\.autodesk\.(?<extension>com|eu|us)$/;

  // Match the URL against the pattern
  const match = pattern.exec(url);

  const { env, region, extension, platform } = match?.groups || {
    env: DEFAULT_ENV,
    region: DEFAULT_REGION,
    platform: PLATFORMS.ACC,
  };

  const isEMEA = extension === EMEA_EXTENSION;
  const envToUse = (
    env === 'stg' ? ENVS.STAGING : env?.toUpperCase()
  ) as keyof typeof ENVS;

  return {
    // eslint-disable-next-line @adsk/r13n-best-practices/no-default-region
    region: isEMEA ? EMEA_REGION : (region?.toUpperCase() ?? DEFAULT_REGION),
    env: ENVS[envToUse] ?? ENVS.PRODUCTION,
    platform: platform as Platform,
    partition: getPartition(url),
  };
}
